import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { parseSchema } from '@repo/utils/parse-schema';
import { minutesToMilliseconds } from '@repo/utils/time';

import { getAccessToken, http } from '../http';

// `/docs/*` live at the gateway root, alongside the `/internal` API prefix.
export const gatewayBase = import.meta.env.VITE_API_BASE.replace(/\/internal\/?$/, '');

// A selectable documentation group: the gateway plus every service the caller may see.
const docServiceSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type DocService = z.infer<typeof docServiceSchema>;

async function getDocServices() {
  return parseSchema(docServiceSchema.array(), await http(`${gatewayBase}/docs/services`));
}

export function queryDocServices() {
  return queryOptions({
    queryFn: getDocServices,
    queryKey: ['developer', 'doc-services'],
    staleTime: minutesToMilliseconds(10),
  });
}

// Sentinel for the combined view: no `services` filter, every group merged.
export const ALL_SERVICES_ID = '__all__';

// The merged OpenAPI document, tiered by the caller's token. One group, or all for the sentinel.
export function docSpecUrl(serviceId: string): string {
  if (serviceId === ALL_SERVICES_ID) {
    return `${gatewayBase}/docs/spec`;
  }
  return `${gatewayBase}/docs/spec?services=${encodeURIComponent(serviceId)}`;
}

// The organisation's guides, rendered by the Authorization service per organisation: the
// onboarding guide (also attached to the invitation) and the API integration guide.
export type GuideKey = 'onboarding' | 'integration';

export const GUIDE_PDF_FILE_NAMES: Record<GuideKey, string> = {
  onboarding: 'northern-lights-onboarding-guide.pdf',
  integration: 'northern-lights-api-integration-guide.pdf',
};

// A self-contained HTML document or a PDF; neither is JSON, so this bypasses `http`.
async function fetchGuideResponse(organizationId: string, guide: GuideKey, format: 'html' | 'pdf'): Promise<Response> {
  const token = await getAccessToken();
  const query = format === 'pdf' ? '?format=pdf' : '';
  const response = await fetch(`${import.meta.env.VITE_API_BASE}/authorization/organizations/${organizationId}/guides/${guide}${query}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(`Failed to load the ${guide} guide (${response.status}).`);
  }
  return response;
}

export async function fetchGuide(organizationId: string, guide: GuideKey): Promise<string> {
  return (await fetchGuideResponse(organizationId, guide, 'html')).text();
}

export async function fetchGuidePdf(organizationId: string, guide: GuideKey): Promise<Blob> {
  return (await fetchGuideResponse(organizationId, guide, 'pdf')).blob();
}

export function queryGuide(organizationId: string, guide: GuideKey) {
  return queryOptions({
    queryKey: ['developer', 'guide', guide, organizationId],
    queryFn: async () => fetchGuide(organizationId, guide),
    staleTime: minutesToMilliseconds(10),
  });
}

// A short, non-reversible stand-in for a token in React Query keys: keys show in devtools and
// would be written out by any cache persister, so the token itself must not be one.
export function tokenFingerprint(token: string): string {
  let hash = 5381;
  for (let i = 0; i < token.length; i++) {
    hash = ((hash << 5) + hash + token.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}
